import { useEffect, useState } from 'react'
import { Typography, Button, MenuItem, useTheme } from '@mui/material'
import { ColStack } from 'components/atoms/container/InCardStack'
import RowStack from 'components/atoms/container/RowStack'
import colors from 'styles/colors'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import EventRowCard from 'components/molecules/myTeam/EventRowCard'
import { getLocalUser } from 'utils/localStorage'
import { getRequest } from 'utils/api'
import { USER_CALENDAR_EVENTS_URL } from 'services/constants'
import StyledAccordion from 'components/atoms/container/Accordion'
import SearchInputBox from 'components/atoms/input/SearchInputBox'
import { StyledSelect } from 'components/atoms/input/StyledInputs'
import { getFullName } from 'utils/formatText'

const RenderGroupedEvents = ({
  groupedEvents,
  filterConnName,
  selectedRole
}) => {
  const user = getLocalUser()

  return (
    <>
      {Object.keys(groupedEvents).map((key, index) => {
        const events = groupedEvents[key]
        const month = moment(key).format('MMMM YYYY')

        return (
          <StyledAccordion
            key={key}
            initialExpanded={index === 0}
            summaryComponent={<Typography variant='h4'>{month}</Typography>}
            sx={{
              padding: 6,
              borderRadius: '8px !important',
              border: `1px solid ${colors.Grey100}`,
              '& .MuiAccordionSummary-root': {
                minHeight: 'auto !important'
              },
              '& .MuiAccordionSummary-content': {
                margin: '0 !important'
              },
              ':before': { display: 'none' }
            }}>
            <ColStack spacing={6} marginTop={6}>
              {events
                .filter(event => {
                  const conn =
                    event?.userConnectionEvent?.userConnection?.connection ||
                    event?.userConnectionEvent?.userConnection
                  const connRelationship =
                    event?.userConnectionEvent?.userConnection?.relationship
                  return (
                    (getFullName(conn, true) || ``)
                      .toLowerCase()
                      .indexOf(filterConnName) >= 0 &&
                    (selectedRole === '' || connRelationship === selectedRole)
                  )
                })
                .map((event, index) => {
                  return (
                    <EventRowCard
                      name={user?.firstName}
                      nameRel={
                        event?.userConnectionEvent?.userConnection?.connection
                          ?.firstName ||
                        event?.userConnectionEvent?.userConnection?.firstName
                      }
                      isNotes={false}
                      isPast={true}
                      key={event?.id}
                      event={event}
                      rowTestId={'meetings-past-event-row'}
                      backgroundColor={
                        index % 2 === 0
                          ? colors.MutedTeal50
                          : colors.MutedTeal100
                      }
                      avatarUser={
                        event?.userConnectionEvent?.userConnection
                          ?.connection ||
                        event?.userConnectionEvent?.userConnection
                      }
                    />
                  )
                })}
            </ColStack>
          </StyledAccordion>
        )
      })}
    </>
  )
}

const PastOneOnOnes = () => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  moment.locale(i18n.resolvedLanguage)
  const [oneOnOneRelationships, setOneOnOneRelationships] = useState([])
  const [filterConnName, setFilterConnName] = useState('')
  const [selectedRole, setSelectedRole] = useState('')
  const [groupedEvents, setGroupedEvents] = useState({})
  const [pagination, setPagination] = useState({ next: 1, total: 0 })
  const [nbVisibleEvents, setNbVisibleEvents] = useState(0)
  const loadTime = moment.utc().toISOString()
  const EVENTS_PER_GROUP = 10

  const paginateEvents = async (
    next = pagination.next,
    pastGroupedEvents = groupedEvents
  ) => {
    if (next) {
      const res = await getRequest(USER_CALENDAR_EVENTS_URL, {
        limit: EVENTS_PER_GROUP,
        page: next,
        historicalEvents: true,
        maxDateTime: loadTime
      })
      if (res.status === 200) {
        let total = res.data.data.pagination?.total

        const newGroupedEvents = { ...pastGroupedEvents }
        const newOneOnOneRelations = new Set(oneOnOneRelationships)

        // Merge with newGroupedEvents
        res.data.data.data.forEach(event => {
          if (event?.userConnectionEvent?.userConnection?.relationship)
            newOneOnOneRelations.add(
              event?.userConnectionEvent?.userConnection?.relationship
            )

          const startTime = moment(event.startTime).format('YYYY-MM')
          if (!newGroupedEvents[startTime]) {
            newGroupedEvents[startTime] = []
          }
          newGroupedEvents[startTime].push(event)
        })
        setOneOnOneRelationships([...newOneOnOneRelations])

        setGroupedEvents(newGroupedEvents)
        setPagination({ ...res.data.data.pagination, total })
      }
    }
  }

  useEffect(() => {
    paginateEvents(1, {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const increaseVisibleEvents = async () => {
    const newNum = nbVisibleEvents + EVENTS_PER_GROUP
    await paginateEvents()
    setNbVisibleEvents(Math.min(pagination?.total, newNum))
  }

  return (
    <ColStack spacing={12}>
      <RowStack spacing={4} width='unset'>
        <SearchInputBox
          onChangeHandler={e => setFilterConnName(e.target.value.toLowerCase())}
          placeholder={t('myTeam.filterConnections')}
          style={{ padding: 0 }}
          inputSx={{
            width: 360,
            '& .MuiInputBase-input': {
              ...theme.typography.body1
            }
          }}
        />

        <StyledSelect
          sx={{ minWidth: 260 }}
          displayEmpty
          value={selectedRole}
          onChange={e => setSelectedRole(e.target.value)}>
          <MenuItem value=''>{t('addRaters.relationship')}</MenuItem>
          {oneOnOneRelationships.map(item => (
            <MenuItem key={item} value={item}>
              {t(`constants.userConnRelationshipPlural.${item}`)}
            </MenuItem>
          ))}
        </StyledSelect>
      </RowStack>

      <ColStack spacing={6} sx={{ maxWidth: 672 }}>
        <RenderGroupedEvents
          groupedEvents={groupedEvents}
          filterConnName={filterConnName}
          selectedRole={selectedRole}
        />

        {pagination?.total !== 0 && (
          <Button
            onClick={increaseVisibleEvents}
            disabled={pagination?.total === nbVisibleEvents}
            variant='outlined'
            size='small'
            color='primary'>
            {t('common.loadMore')}
          </Button>
        )}
      </ColStack>
    </ColStack>
  )
}

export default PastOneOnOnes
